import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { STORAGE_KEYS } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";

const PropertyContext = createContext(null);

export function PropertyProvider({ children }) {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [currentId, setCurrentId] = useState(() => localStorage.getItem(STORAGE_KEYS.propertyId) || null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setProperties([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/properties");
      setProperties(data);
      if (data.length > 0) {
        const stored = localStorage.getItem(STORAGE_KEYS.propertyId);
        const hit = data.find(p => p.id === stored);
        if (!hit) {
          localStorage.setItem(STORAGE_KEYS.propertyId, data[0].id);
          setCurrentId(data[0].id);
        } else {
          setCurrentId(stored);
        }
      }
    } catch (err) {
      // 402 = account sospeso: gestito da BillingContext (banner + redirect).
      // Altri errori vengono ignorati silenziosamente perché l'utente non
      // necessita di properties quando la sessione non è valida o è bloccata.
      if (err?.response?.status !== 402 && err?.response?.status !== 401) {
        // log strettamente diagnostico, niente sensibili
      }
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const select = useCallback((id) => {
    localStorage.setItem(STORAGE_KEYS.propertyId, id);
    setCurrentId(id);
    window.location.reload();
  }, []);

  const current = useMemo(
    () => properties.find(p => p.id === currentId) || properties[0] || null,
    [properties, currentId]
  );

  const value = useMemo(
    () => ({ properties, current, loading, reload: load, select }),
    [properties, current, loading, load, select]
  );

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export const useProperty = () => useContext(PropertyContext);

